import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export interface HoverInfo {
  type: 'zone' | 'occupant' | 'wall'
  name: string
  detail?: string
  x: number
  y: number
}

const ANIMATION_DURATION = 500 // ms

export class ControlsEngine {
  orbitControls: OrbitControls
  raycaster: THREE.Raycaster
  private mouse = new THREE.Vector2()
  private camera: THREE.OrthographicCamera
  private domElement: HTMLElement
  private dragTarget: THREE.Object3D | null = null
  private dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
  private dragOffset = new THREE.Vector3()
  private mouseDownPos = new THREE.Vector2()
  private isClick = false

  // Camera animation state
  private cameraAnimating = false
  private cameraAnimStart = 0
  private cameraStartPos = new THREE.Vector3()
  private cameraEndPos = new THREE.Vector3()
  private cameraStartTarget = new THREE.Vector3()
  private cameraEndTarget = new THREE.Vector3()

  // Hover state
  private lastHoveredId: string | null = null

  // Handle drag state (Phase 2)
  private activeHandle: { type: 'left' | 'right' | 'rotate'; startMouse: THREE.Vector2 } | null = null
  private lastDragPoint: THREE.Vector3 = new THREE.Vector3()
  private handleTargets: THREE.Object3D[] = []

  editMode: 'none' | 'zone' | 'wall' | 'path' = 'none'

  onZoneClick?: (zoneId: string) => void
  onZoneHover?: (zoneId: string | null) => void
  onOccupantClick?: (badgeCode: string) => void
  onOccupantHover?: (badgeCode: string | null) => void
  onWallClick?: (wallId: string) => void
  onWallHover?: (wallId: string | null) => void
  onHoverChange?: (info: HoverInfo | null) => void
  onZoneDragEnd?: (zoneId: string, x: number, z: number) => void
  onPlaneClick?: (x: number, z: number, floor: number) => void
  onPathSegmentClick?: (segmentId: string) => void
  onHandleDrag?: (handleType: 'left' | 'right' | 'rotate', deltaX: number, deltaZ: number) => void
  onHandleDragEnd?: (handleType: 'left' | 'right' | 'rotate') => void

  constructor(camera: THREE.OrthographicCamera, domElement: HTMLElement) {
    this.camera = camera
    this.domElement = domElement
    this.orbitControls = new OrbitControls(camera, domElement)
    this.orbitControls.enableDamping = true
    this.orbitControls.dampingFactor = 0.1
    this.orbitControls.minZoom = 0.5
    this.orbitControls.maxZoom = 10

    this.raycaster = new THREE.Raycaster()

    this.onMouseDown = this.onMouseDown.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.onMouseUp = this.onMouseUp.bind(this)
    this.onContextMenu = this.onContextMenu.bind(this)

    domElement.addEventListener('mousedown', this.onMouseDown)
    domElement.addEventListener('mousemove', this.onMouseMove)
    domElement.addEventListener('mouseup', this.onMouseUp)
    domElement.addEventListener('contextmenu', this.onContextMenu)
  }

  private scene: THREE.Scene | null = null
  setScene(scene: THREE.Scene) {
    this.scene = scene
  }

  setHandleTargets(targets: THREE.Object3D[]) {
    this.handleTargets = targets
  }

  private onMouseDown(event: MouseEvent) {
    if (event.button !== 0) return
    this.mouseDownPos.set(event.clientX, event.clientY)
    this.isClick = true

    if (this.editMode === 'none') return
    if (!this.scene) return

    const mouse = this.getNormalizedMouse(event, this.domElement)
    this.raycaster.setFromCamera(mouse, this.camera)

    // Check handles first (Phase 2)
    if (this.handleTargets.length > 0) {
      const handleHits = this.raycaster.intersectObjects(this.handleTargets, true)
      for (const hit of handleHits) {
        const handleObj = this.findHandleParent(hit.object)
        if (handleObj) {
          const handleType = handleObj.userData.handleType as 'left' | 'right' | 'rotate'
          this.activeHandle = { type: handleType, startMouse: mouse.clone() }
          this.orbitControls.enabled = false
          const point = new THREE.Vector3()
          this.raycaster.ray.intersectPlane(this.dragPlane, point)
          this.lastDragPoint.copy(point)
          return
        }
      }
    }

    const intersects = this.raycaster.intersectObjects(this.scene.children, true)

    for (const hit of intersects) {
      const obj = this.findUserDataParent(hit.object, this.editMode === 'zone' ? 'zone' : 'wall')
      if (obj) {
        this.dragTarget = obj
        this.orbitControls.enabled = false
        const point = new THREE.Vector3()
        this.raycaster.ray.intersectPlane(this.dragPlane, point)
        this.dragOffset.subVectors(obj.position, point)
        return
      }
    }
  }

  private onMouseMove(event: MouseEvent) {
    // Check if mouse moved enough to not be a click
    if (this.isClick) {
      const dx = event.clientX - this.mouseDownPos.x
      const dy = event.clientY - this.mouseDownPos.y
      if (dx * dx + dy * dy > 9) {
        this.isClick = false
      }
    }

    // Handle drag for wall handles (Phase 2)
    if (this.activeHandle) {
      const mouse = this.getNormalizedMouse(event, this.domElement)
      this.raycaster.setFromCamera(mouse, this.camera)
      const point = new THREE.Vector3()
      if (this.raycaster.ray.intersectPlane(this.dragPlane, point)) {
        const deltaX = point.x - this.lastDragPoint.x
        const deltaZ = point.z - this.lastDragPoint.z
        this.lastDragPoint.copy(point)
        this.onHandleDrag?.(this.activeHandle.type, deltaX, deltaZ)
      }
      return
    }

    // Handle drag in edit mode
    if (this.dragTarget && this.scene) {
      const mouse = this.getNormalizedMouse(event, this.domElement)
      this.raycaster.setFromCamera(mouse, this.camera)
      const point = new THREE.Vector3()
      if (this.raycaster.ray.intersectPlane(this.dragPlane, point)) {
        this.dragTarget.position.x = point.x + this.dragOffset.x
        this.dragTarget.position.z = point.z + this.dragOffset.z
      }
      return
    }

    // Hover detection (both edit and viewer mode)
    if (!this.scene) return
    const mouse = this.getNormalizedMouse(event, this.domElement)
    this.raycaster.setFromCamera(mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.scene.children, true)

    let foundHover = false
    for (const hit of intersects) {
      // Check zones
      const zoneObj = this.findUserDataParent(hit.object, 'zone')
      if (zoneObj?.userData.zoneId) {
        const zoneId = zoneObj.userData.zoneId as string
        const zoneName = (zoneObj.userData.name as string) ?? zoneId
        const zoneDetail = (zoneObj.userData.zoneType as string) || ''
        if (this.lastHoveredId !== zoneId) {
          this.lastHoveredId = zoneId
          this.onZoneHover?.(zoneId)
          this.onHoverChange?.({ type: 'zone', name: zoneName, detail: zoneDetail, x: event.clientX, y: event.clientY })
        } else {
          this.onHoverChange?.({ type: 'zone', name: zoneName, detail: zoneDetail, x: event.clientX, y: event.clientY })
        }
        foundHover = true
        break
      }
      // Check occupants
      const occObj = this.findUserDataParent(hit.object, 'occupant')
      if (occObj?.userData.badgeCode) {
        const badgeCode = occObj.userData.badgeCode as string
        const occName = (occObj.userData.name as string) ?? badgeCode
        const occDetail = (occObj.userData.badgeCode as string) || ''
        if (this.lastHoveredId !== badgeCode) {
          this.lastHoveredId = badgeCode
          this.onOccupantHover?.(badgeCode)
          this.onHoverChange?.({ type: 'occupant', name: occName, detail: occDetail, x: event.clientX, y: event.clientY })
        } else {
          this.onHoverChange?.({ type: 'occupant', name: occName, detail: occDetail, x: event.clientX, y: event.clientY })
        }
        foundHover = true
        break
      }
      // Check walls
      const wallObj = this.findUserDataParent(hit.object, 'wall')
      if (wallObj?.userData.wallId) {
        const wallId = wallObj.userData.wallId as string
        if (this.lastHoveredId !== wallId) {
          this.lastHoveredId = wallId
          this.onWallHover?.(wallId)
          this.onHoverChange?.({ type: 'wall', name: wallId, detail: 'Mur', x: event.clientX, y: event.clientY })
        } else {
          this.onHoverChange?.({ type: 'wall', name: wallId, detail: 'Mur', x: event.clientX, y: event.clientY })
        }
        foundHover = true
        break
      }
    }

    if (!foundHover && this.lastHoveredId !== null) {
      this.lastHoveredId = null
      this.onZoneHover?.(null)
      this.onOccupantHover?.(null)
      this.onWallHover?.(null)
      this.onHoverChange?.(null)
    }
  }

  private onMouseUp(event: MouseEvent) {
    // Handle drag end (Phase 2)
    if (this.activeHandle) {
      this.onHandleDragEnd?.(this.activeHandle.type)
      this.activeHandle = null
      this.orbitControls.enabled = true
      return
    }

    if (this.dragTarget) {
      const userData = this.dragTarget.userData
      if (userData.type === 'zone' && userData.zoneId) {
        this.onZoneDragEnd?.(userData.zoneId as string, this.dragTarget.position.x, this.dragTarget.position.z)
      }
      this.dragTarget = null
      this.orbitControls.enabled = true
      return
    }

    // Click detection in viewer mode (editMode === 'none') or edit mode
    if (this.isClick && event.button === 0 && this.scene) {
      this.handleClick(event, this.scene)
    }
    this.isClick = false
  }

  private onContextMenu(event: MouseEvent) {
    if (this.editMode !== 'none') {
      event.preventDefault()
    }
  }

  private findUserDataParent(obj: THREE.Object3D, type: string): THREE.Object3D | null {
    let current: THREE.Object3D | null = obj
    while (current) {
      if (current.userData?.type === type) return current
      current = current.parent
    }
    return null
  }

  private findHandleParent(obj: THREE.Object3D): THREE.Object3D | null {
    let current: THREE.Object3D | null = obj
    while (current) {
      if (current.userData?.isHandle) return current
      current = current.parent
    }
    return null
  }

  handleClick(event: MouseEvent, scene: THREE.Scene) {
    const mouse = this.getNormalizedMouse(event, this.domElement)
    this.raycaster.setFromCamera(mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(scene.children, true)

    for (const hit of intersects) {
      // Check zones
      const zoneObj = this.findUserDataParent(hit.object, 'zone')
      if (zoneObj?.userData.zoneId) {
        this.onZoneClick?.(zoneObj.userData.zoneId as string)
        return
      }
      // Check occupants
      const occObj = this.findUserDataParent(hit.object, 'occupant')
      if (occObj?.userData.badgeCode) {
        this.onOccupantClick?.(occObj.userData.badgeCode as string)
        return
      }
      // Check walls
      const wallObj = this.findUserDataParent(hit.object, 'wall')
      if (wallObj?.userData.wallId) {
        this.onWallClick?.(wallObj.userData.wallId as string)
        return
      }
      // Check path segments
      const segObj = this.findUserDataParent(hit.object, 'pathSegment')
      if (segObj?.userData.segmentId) {
        this.onPathSegmentClick?.(segObj.userData.segmentId as string)
        return
      }
    }

    // If in path edit mode and nothing was hit, dispatch plane click for placement
    if (this.editMode === 'path') {
      const point = new THREE.Vector3()
      if (this.raycaster.ray.intersectPlane(this.dragPlane, point)) {
        // Determine floor from hit
        let floor = 0
        for (const hit of intersects) {
          const floorGroup = this.findFloorParent(hit.object)
          if (floorGroup?.userData.floorIndex !== undefined) {
            floor = floorGroup.userData.floorIndex as number
            break
          }
        }
        this.onPlaneClick?.(point.x, point.z, floor)
      }
    }
  }

  private findFloorParent(obj: THREE.Object3D): THREE.Object3D | null {
    let current: THREE.Object3D | null = obj
    while (current) {
      if (current.userData?.floorIndex !== undefined) return current
      current = current.parent
    }
    return null
  }

  /**
   * Animate the camera position and orbit target to focus on a given world position.
   */
  focusOnPosition(x: number, y: number, z: number) {
    const target = new THREE.Vector3(x, y, z)
    const cameraOffset = new THREE.Vector3().subVectors(this.camera.position, this.orbitControls.target)

    this.cameraStartPos.copy(this.camera.position)
    this.cameraEndPos.copy(target).add(cameraOffset)
    this.cameraStartTarget.copy(this.orbitControls.target)
    this.cameraEndTarget.copy(target)
    this.cameraAnimStart = performance.now()
    this.cameraAnimating = true
  }

  /**
   * Animate camera to 2D top-down or 3D isometric view.
   */
  animateTo2D(enable2D: boolean) {
    const currentTarget = this.orbitControls.target.clone()

    if (enable2D) {
      this.cameraStartPos.copy(this.camera.position)
      this.cameraEndPos.set(currentTarget.x, 100, currentTarget.z)
      this.cameraStartTarget.copy(currentTarget)
      this.cameraEndTarget.copy(currentTarget).setY(0)
    } else {
      this.cameraStartPos.copy(this.camera.position)
      this.cameraEndPos.set(currentTarget.x + 50, 50, currentTarget.z + 50)
      this.cameraStartTarget.copy(currentTarget)
      this.cameraEndTarget.set(currentTarget.x, 15, currentTarget.z)
    }

    this.cameraAnimStart = performance.now()
    this.cameraAnimating = true
  }

  update() {
    // Handle camera animation
    if (this.cameraAnimating) {
      const elapsed = performance.now() - this.cameraAnimStart
      const t = Math.min(elapsed / ANIMATION_DURATION, 1)
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3)

      this.camera.position.lerpVectors(this.cameraStartPos, this.cameraEndPos, eased)
      this.orbitControls.target.lerpVectors(this.cameraStartTarget, this.cameraEndTarget, eased)
      this.camera.updateProjectionMatrix()

      if (t >= 1) {
        this.cameraAnimating = false
      }
    }

    this.orbitControls.update()
  }

  raycast(mouse: THREE.Vector2, camera: THREE.Camera, objects: THREE.Object3D[]): THREE.Intersection[] {
    this.raycaster.setFromCamera(mouse, camera)
    return this.raycaster.intersectObjects(objects, true)
  }

  getNormalizedMouse(event: MouseEvent, domElement: HTMLElement): THREE.Vector2 {
    const rect = domElement.getBoundingClientRect()
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
    return this.mouse
  }

  dispose() {
    this.domElement.removeEventListener('mousedown', this.onMouseDown)
    this.domElement.removeEventListener('mousemove', this.onMouseMove)
    this.domElement.removeEventListener('mouseup', this.onMouseUp)
    this.domElement.removeEventListener('contextmenu', this.onContextMenu)
    this.orbitControls.dispose()
  }
}
